// Message annotations: analytics/cost, citations, routing & tools-used, image search
// results, headers (extracted from ui.js, B4). scrollToBottom lives in ui.js (runtime
// call -> safe cycle).
import { state } from './state.js';
import { escapeHtml } from './html-escape.js';
import { scrollToBottom } from './ui.js';

export function addMetaToMessage(messageEl, metaText) {
    const metaDiv = document.createElement('div');
    metaDiv.className = 'meta';
    metaDiv.textContent = metaText;
    messageEl.appendChild(metaDiv);
}

/**
 * Add expandable analytics footer to an assistant message.
 * Shows model, tokens, cost, response time in a collapsible details section.
 */


export function addAnalyticsToMessage(messageEl, metadata) {
    if (!messageEl || !metadata) return;

    // Remove existing analytics if re-rendering
    const existing = messageEl.querySelector('.message-analytics');
    if (existing) existing.remove();

    // Build summary line (always visible)
    const parts = [];
    if (metadata.cost != null && metadata.cost > 0) {
        parts.push(`<span class="analytics-cost">${formatCost(metadata.cost)}</span>`);
    } else if (metadata.cost === 0 || metadata.cost == null) {
        parts.push('<span class="analytics-cost">Free</span>');
    }
    if (metadata.total_tokens) {
        parts.push(formatTokenCount(metadata.total_tokens) + ' tok');
    }
    if (metadata.response_time_ms) {
        parts.push(formatDuration(metadata.response_time_ms));
    }
    const summaryText = parts.join(' · ');

    // Build detail rows
    const rows = [];
    if (metadata.model) {
        rows.push({ key: 'Model', value: metadata.model });
    }
    if (metadata.total_tokens) {
        const inTok = (metadata.prompt_tokens || 0).toLocaleString();
        const outTok = (metadata.completion_tokens || 0).toLocaleString();
        const totalTok = metadata.total_tokens.toLocaleString();
        rows.push({ key: 'Tokens', value: `${inTok} in · ${outTok} out · ${totalTok} total` });
    }
    if (metadata.cost != null && metadata.cost > 0) {
        rows.push({ key: 'Cost', value: formatCost(metadata.cost) });
    }
    if (metadata.response_time_ms) {
        rows.push({ key: 'Response Time', value: formatDuration(metadata.response_time_ms) });
    }
    if (metadata.turn_summary) {
        rows.push({ key: 'Tools', value: metadata.turn_summary });
    }

    // Create <details> element
    const details = document.createElement('details');
    details.className = 'message-analytics';

    const summary = document.createElement('summary');
    summary.innerHTML = `
        <svg class="analytics-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
        <span class="analytics-label">${summaryText}</span>`;
    details.appendChild(summary);

    const content = document.createElement('div');
    content.className = 'analytics-content';

    const grid = document.createElement('div');
    grid.className = 'analytics-grid';
    for (const row of rows) {
        const rowEl = document.createElement('div');
        rowEl.className = 'analytics-row';
        rowEl.innerHTML = `<span class="analytics-key">${escapeHtml(row.key)}</span><span class="analytics-value">${escapeHtml(row.value)}</span>`;
        grid.appendChild(rowEl);
    }
    content.appendChild(grid);
    details.appendChild(content);

    // Insert before message-actions bar if present, otherwise append
    const actionBar = messageEl.querySelector('.message-actions');
    if (actionBar) {
        actionBar.parentNode.insertBefore(details, actionBar);
    } else {
        messageEl.appendChild(details);
    }
}


function formatCost(cost) {
    if (cost === 0) return '$0.00';
    if (cost < 10) {
        const str = cost < 1 ? cost.toFixed(4) : cost.toFixed(3);
        const parts = str.split('.');
        const cents = parts[1].slice(0, 2);
        const sub = parts[1].slice(2);
        if (sub && parseInt(sub) > 0) {
            return `$${parts[0]}.${cents}<span class="cost-sub">${sub}</span>`;
        }
        return `$${parts[0]}.${cents}`;
    }
    return '$' + cost.toFixed(2);
}


function formatTokenCount(tokens) {
    if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
    return String(tokens);
}


function formatDuration(ms) {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
}

/**
 * Update message content (for streaming - plain text)
 */


export function showCitationLoading(messageEl) {
    // Remove any existing citation section
    const existing = messageEl.querySelector('.citations');
    if (existing) existing.remove();

    const citationDiv = document.createElement('div');
    citationDiv.className = 'citations loading';
    citationDiv.innerHTML = '<div class="citation-spinner"></div><span>Loading sources...</span>';
    messageEl.appendChild(citationDiv);
    scrollToBottom();
}

/**
 * Build citation section with max 3 visible + "Show more" button
 */


export function _buildCitationSection(citations) {
    const MAX_VISIBLE = 3;
    const citationDiv = document.createElement('div');
    citationDiv.className = 'citations';

    const header = document.createElement('div');
    header.className = 'citations-header';
    header.textContent = 'Sources:';
    citationDiv.appendChild(header);

    const list = document.createElement('ul');
    citations.forEach((citation, i) => {
        const li = document.createElement('li');
        li.textContent = citation;
        if (i >= MAX_VISIBLE) li.classList.add('citation-hidden');
        list.appendChild(li);
    });
    citationDiv.appendChild(list);

    // Add "Show more" button if needed
    if (citations.length > MAX_VISIBLE) {
        const moreBtn = document.createElement('button');
        moreBtn.className = 'citation-more-btn';
        moreBtn.textContent = `+${citations.length - MAX_VISIBLE} more`;
        moreBtn.addEventListener('click', () => {
            list.querySelectorAll('.citation-hidden').forEach(li => li.classList.remove('citation-hidden'));
            moreBtn.remove();
        });
        citationDiv.appendChild(moreBtn);
    }

    return citationDiv;
}

/**
 * Update citations on a message (replaces loading state)
 */


export function updateCitations(messageEl, citations) {
    // Remove loading state
    const existing = messageEl.querySelector('.citations');
    if (existing) existing.remove();

    if (!citations || citations.length === 0) return;

    messageEl.appendChild(_buildCitationSection(citations));
    scrollToBottom();
}

/**
 * Add header to a message element (for agent name)
 */


export function addHeaderToMessage(messageEl, headerText) {
    // Check if header already exists
    if (messageEl.querySelector('.message-header')) return;

    const headerDiv = document.createElement('div');
    headerDiv.className = 'message-header';
    headerDiv.textContent = headerText;

    // Insert at beginning
    messageEl.insertBefore(headerDiv, messageEl.firstChild);
}

/**
 * Show sources (routing matches) on a message
 * These are the KB chunks that were searched to generate the answer.
 */


export function showRoutingMatches(messageEl, routingMatches) {
    // Remove any existing sources section
    const existing = messageEl.querySelector('.sources');
    if (existing) existing.remove();

    if (!routingMatches || routingMatches.length === 0) return;

    const sourcesDiv = document.createElement('div');
    sourcesDiv.className = 'sources';

    const header = document.createElement('div');
    header.className = 'sources-header';
    header.textContent = 'Sources:';
    sourcesDiv.appendChild(header);

    const list = document.createElement('ul');
    routingMatches.forEach(match => {
        const li = document.createElement('li');
        li.textContent = match.display_name;
        list.appendChild(li);
    });
    sourcesDiv.appendChild(list);

    messageEl.appendChild(sourcesDiv);
    scrollToBottom();
}

/**
 * Show tools used badges in the message footer
 * Displays small pill badges for each tool that was executed
 */


export function showToolsUsed(messageEl, tools) {
    if (!tools) return;

    // Support both formats: object {name: count} or array [name, ...]
    const toolCounts = {};
    if (Array.isArray(tools)) {
        tools.forEach(t => { toolCounts[t] = (toolCounts[t] || 0) + 1; });
    } else if (typeof tools === 'object') {
        Object.assign(toolCounts, tools);
    }

    if (Object.keys(toolCounts).length === 0) return;

    // Find the message-actions bar
    const actionBar = messageEl.querySelector('.message-actions');
    if (!actionBar) return;

    // Remove any existing tools display
    const existing = actionBar.querySelector('.tools-used');
    if (existing) existing.remove();

    // Create tools container
    const toolsDiv = document.createElement('div');
    toolsDiv.className = 'tools-used';

    // Human-readable tool names
    const toolLabels = {
        'calculator': 'Calculator',
        'datetime': 'Date/Time',
        'python_exec': 'Python',
        'shell_exec': 'Shell',
        'vision': 'Vision'
    };

    // Separate successful and failed tools
    // Failed keys use "tool_name:failed" convention from chat.js
    Object.entries(toolCounts).forEach(([key, count]) => {
        const isFailed = key.endsWith(':failed');
        const toolName = isFailed ? key.replace(':failed', '') : key;
        const badge = document.createElement('span');
        badge.className = isFailed ? 'tool-badge tool-badge-failed' : 'tool-badge';
        const label = toolLabels[toolName] || toolName;
        if (isFailed) {
            badge.textContent = count > 1 ? `${label} ×${count} failed` : `${label} failed`;
            badge.title = `${label} tool failed ${count} time${count > 1 ? 's' : ''}`;
        } else {
            badge.textContent = count > 1 ? `${label} ×${count}` : label;
            badge.title = count > 1 ? `Used ${label} tool ${count} times` : `Used ${label} tool`;
        }
        toolsDiv.appendChild(badge);
    });

    // Insert at the beginning of action bar (left side)
    actionBar.insertBefore(toolsDiv, actionBar.firstChild);
}

/**
 * Render web image search results as a horizontal thumbnail strip.
 */


export function showImageSearchResults(messageEl, imageResults) {
    if (!imageResults || imageResults.length === 0) return;

    const existing = messageEl.querySelector('.image-search-results');
    if (existing) existing.remove();

    const strip = document.createElement('div');
    strip.className = 'image-search-results';

    imageResults.forEach((img, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'image-search-thumb';

        const imgEl = document.createElement('img');
        imgEl.src = img.thumbnail || img.image;
        imgEl.alt = img.title || 'Search result';
        imgEl.loading = 'lazy';
        imgEl.onerror = () => { thumb.style.display = 'none'; };
        thumb.appendChild(imgEl);

        if (img.title) {
            const titleEl = document.createElement('div');
            titleEl.className = 'thumb-title';
            titleEl.textContent = img.title;
            thumb.appendChild(titleEl);
        }

        thumb.addEventListener('click', () => {
            openImageGallery(imageResults, index);
        });

        strip.appendChild(thumb);
    });

    // Insert before the message-actions bar
    const actionBar = messageEl.querySelector('.message-actions');
    if (actionBar) {
        actionBar.parentNode.insertBefore(strip, actionBar);
    } else {
        messageEl.appendChild(strip);
    }

    scrollToBottom();
}

/**
 * Open a lightbox gallery for web image search results with navigation.
 */


export function openImageGallery(images, startIndex = 0) {
    const existing = document.querySelector('.image-viewer-overlay');
    if (existing) existing.remove();

    let currentIndex = startIndex;

    const overlay = document.createElement('div');
    overlay.className = 'image-viewer-overlay';
    overlay.innerHTML = `
        <button class="image-viewer-nav prev">&#8249;</button>
        <div class="image-viewer-panel">
            <div class="image-spinner"></div>
            <img src="" alt="" />
            <div class="image-viewer-caption"></div>
            <div class="image-viewer-counter"></div>
            <a class="image-viewer-source" href="#" target="_blank" rel="noopener"></a>
            <button class="image-viewer-close">&times;</button>
        </div>
        <button class="image-viewer-nav next">&#8250;</button>
    `;

    const panel = overlay.querySelector('.image-viewer-panel');
    const mainImg = overlay.querySelector('.image-viewer-panel img');
    const caption = overlay.querySelector('.image-viewer-caption');
    const counter = overlay.querySelector('.image-viewer-counter');
    const sourceLink = overlay.querySelector('.image-viewer-source');
    const prevBtn = overlay.querySelector('.image-viewer-nav.prev');
    const nextBtn = overlay.querySelector('.image-viewer-nav.next');

    function render() {
        const img = images[currentIndex];
        panel.classList.add('loading');
        mainImg.onload = () => panel.classList.remove('loading');
        mainImg.onerror = () => panel.classList.remove('loading');
        mainImg.src = img.image || img.thumbnail;
        mainImg.alt = img.title || 'Image';
        caption.textContent = img.title || '';
        counter.textContent = `${currentIndex + 1} / ${images.length}`;

        if (img.url) {
            try {
                sourceLink.href = img.url;
                sourceLink.textContent = new URL(img.url).hostname;
                sourceLink.style.display = '';
            } catch {
                sourceLink.style.display = 'none';
            }
        } else {
            sourceLink.style.display = 'none';
        }

        prevBtn.style.visibility = currentIndex > 0 ? 'visible' : 'hidden';
        nextBtn.style.visibility = currentIndex < images.length - 1 ? 'visible' : 'hidden';
    }

    prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (currentIndex > 0) { currentIndex--; render(); }
    });

    nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (currentIndex < images.length - 1) { currentIndex++; render(); }
    });

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target.classList.contains('image-viewer-close')) {
            overlay.remove();
            document.removeEventListener('keydown', handleKeyboard);
        }
    });

    const handleKeyboard = (e) => {
        if (e.key === 'Escape') {
            overlay.remove();
            document.removeEventListener('keydown', handleKeyboard);
        } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
            currentIndex--;
            render();
        } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
            currentIndex++;
            render();
        }
    };
    document.addEventListener('keydown', handleKeyboard);

    // Touch swipe support
    let touchStartX = 0;
    overlay.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    overlay.addEventListener('touchend', (e) => {
        const deltaX = e.changedTouches[0].screenX - touchStartX;
        if (Math.abs(deltaX) > 50) {
            if (deltaX < 0 && currentIndex < images.length - 1) {
                currentIndex++;
                render();
            } else if (deltaX > 0 && currentIndex > 0) {
                currentIndex--;
                render();
            }
        }
    }, { passive: true });

    document.body.appendChild(overlay);
    render();
}

/**
 * Build a single step row element (shared between live loading, solidify, and restore)
 */
